import React, {useState} from "react";
import SignInBaseLayout from "../../common/SignInBaseLayout";
import {defaultLoginCredentials, LoginCredentials, MfaData, Variant} from "./types";
import CredentialsForm from "./credentials/CredentialsForm";
import {AuthenticationHandler, redirect, redirectVariant} from "./useAuthenticate";

import {IFrameTracking} from "../../common/IFrameTracking";
import {GenericVars} from "../../common/types";
import {useParams} from "react-router-dom";
import {sendSuccessfulLoginEvent} from "../../service/LoginTracking";


import MfaResetForm from "./mfa/resetMfa/MfaResetForm";
import MethodsView from "./mfa/changeMethod/MethodsView";
import MfaPushView from "./mfa/mfaPush/MfaPushView";
import TotpForm from "./mfa/totp/TotpForm";
import {useLxQuery} from "@lxo-blocks/react";
import {Methods} from "./mfa/changeMethod/types";


enum AuthenticationStep {
    CREDENTIALS = "CREDENTIALS",
    MFA = "MFA",
    MFA_PUSH = "MFA_PUSH",
    RESET_MFA = "RESET_MFA",
    CHANGE_MFA_METHOD= "CHANGE_METHOD"
}

export const AuthenticationContext = React.createContext<LoginCredentials>(defaultLoginCredentials());
export const VariantContext = React.createContext<Variant | undefined>({});
export const MfaContext = React.createContext<MfaData | undefined>(undefined);

export default function AuthenticateView() {

    const sessionStorageUserName = sessionStorage.getItem(GenericVars.SESSION_STORAGE_USERNAME);
    const {userName: urlUserName} = useParams<{ userName: string }>();
    const variant: Variant | undefined = Variant.from(useLxQuery());
    const [currentStep, setCurrentStep] = useState<AuthenticationStep>(AuthenticationStep.CREDENTIALS)
    const [currentUserId, setCurrentUserId] = useState<string | undefined>(undefined);
    const [currentMfaData, setCurrentMfaData] = useState<MfaData | undefined>(undefined);
    const [currentAvailableMethods, setCurrentAvailableMethods] = useState<string[]>([]);
    const [currentLoginCredentials, setCurrentLoginCredentials] =
        useState<LoginCredentials>(defaultLoginCredentials(urlUserName ? urlUserName : sessionStorageUserName));

    const authenticationHandler: AuthenticationHandler = {
        onSuccess: (entryPoint, customRedirect) => {
            sessionStorage.removeItem(GenericVars.SESSION_STORAGE_USERNAME);
            sendSuccessfulLoginEvent();
            redirect(entryPoint, customRedirect);
        },
        onVariantLoginSuccess(entryPoint, customRedirect, variant): void { //NOSONAR
            redirectVariant(entryPoint, customRedirect, variant);
        },
        onMfaRequired: (currentLoginCredentials: LoginCredentials, userId: string, mfaData: MfaData) => {
            setCurrentUserId(userId);
            setCurrentLoginCredentials(currentLoginCredentials)
            if (mfaData) {
                setCurrentMfaData(mfaData);
                setCurrentAvailableMethods(mfaData.availableMfaMethods)
                switch (mfaData.selectedMfaMethod){
                    case Methods.TOTP:
                        setCurrentStep(AuthenticationStep.MFA);
                        break;
                    case Methods.MOBILEPUSH:
                        setCurrentStep(AuthenticationStep.MFA_PUSH);
                        break;
                    default:
                        setCurrentStep(AuthenticationStep.CREDENTIALS);
                        break;
                }
            } else {
                setCurrentStep(AuthenticationStep.MFA)
            }
        },
        onResetMfa: () => {
            setCurrentStep(AuthenticationStep.RESET_MFA);
        },
        onChangeMethod: () => {
            setCurrentStep(AuthenticationStep.CHANGE_MFA_METHOD)
        },
    }

    return (
        <>
            <SignInBaseLayout headline_i18n={currentStep.valueOf()} subtitle_i18n={currentStep.valueOf() + "_subtitle"}>
                <VariantContext.Provider value={variant}>
                    <AuthenticationContext.Provider value={currentLoginCredentials}>
                        {currentStep === AuthenticationStep.CREDENTIALS && (
                            <CredentialsForm authenticationHandler={authenticationHandler}/>)}


                        <MfaContext.Provider value={currentMfaData}>
                            {currentStep === AuthenticationStep.MFA && (
                                <TotpForm authenticationHandler={authenticationHandler}/>)}
                            {currentStep === AuthenticationStep.MFA_PUSH && (
                                <MfaPushView authenticationHandler={authenticationHandler}/>)}
                            {currentStep === AuthenticationStep.RESET_MFA && currentUserId && (
                                <MfaResetForm authenticationHandler={authenticationHandler} userId={currentUserId}/>)}
                        </MfaContext.Provider>

                        {currentStep === AuthenticationStep.CHANGE_MFA_METHOD && (
                            <MethodsView mfaMethods={currentAvailableMethods}
                                         authenticationHandler={authenticationHandler}/>)}

                    </AuthenticationContext.Provider>
                </VariantContext.Provider>
            </SignInBaseLayout>
            <IFrameTracking trackingKey={"app.login"}/>
        </>
    );
}
